import React from "react";
import ModelSubscriptions from "./ModelSubscriptions";

/**
 * I am a React HoC in the style of Flux's Container, but based on data stored
 * in Backbone Models. I accept the same three callbacks as Flux's Container's
 * `createFunctional`, but with slightly different semantics.
 *
 * The `getModels` callback should return an array of Watchable, which are
 * either an object that mixes in Backbone.Events, or an array containing such
 * an object followed by N event names. If no event names are passed, "all" is
 * implied.
 *
 * The `calculateState` callback receives an extra third argument for
 * registering one-shot Watchables from "further down the tree". Since Backbone
 * provides no way to aggregate events, they must be listened for at whatever
 * level emits them. One-shot Watchables registered this way will, when fired,
 * retrigger the entire container, which mirrors the way Flux's Container works,
 * and how React's default "same" checks operate.
 *
 * Unlike Flux's Container, props are always passed through, as a Backbone-based
 * container will need to get it's Model(s) from somewhere.
 *
 * @param viewFn
 * @param getModels
 * @param calculateState
 * @return {BackboneContainer}
 */
// Watchable := Model | MultiTuple<model, event...>
const createFunctional = (
    viewFn, // props -> element
    getModels, // props -> list<Watchable>
    calculateState, // prevState -> props -> (list<Watchable> -> void) -> nextProps
) => {

    class BackboneContainer extends React.PureComponent {
        constructor(props) {
            super(props);
            this.subscriptions = new ModelSubscriptions();
            this.oneShot = watchable =>
                this.subscriptions.addOneShotWatchable(watchable);
            this.subscriptions.setCallback(() => this.setState(
                (prevState, props) => calculateState(
                    prevState, props, this.oneShot,
                ),
            ));
            this.subscriptions.setWatchables(getModels(props) || []);
            this.state = calculateState(
                undefined, props, this.oneShot,
            );
        }

        UNSAFE_componentWillReceiveProps(nextProps) {
            this.subscriptions.setWatchables(getModels(nextProps));
            this.setState(
                prevState => calculateState(
                    prevState, nextProps, this.oneShot,
                ),
            );
        }

        componentWillUnmount() {
            this.subscriptions.reset();
        }

        render() {
            return viewFn(this.state);
        }
    }

    return BackboneContainer;
};

export default {createFunctional};



// WEBPACK FOOTER //
// ./app/data/util/BackboneContainer.js